import React, { useEffect, useRef, useState } from 'react';
import { Backdrop, Box, Fade, Modal, Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { CSSProperties } from '@mui/styles';
import classNames from 'classnames';
import { WelcomeMessagePlayerProps } from './WelcomeMessagePlayer';
import RemoveAction from '../../../RemoveAction';

const INITIAL_TIMEOUT = 250;
const TRANSITION_DURATION = 1000;

const useStyles = makeStyles((theme: Theme) => ({
  cloneHidden: {
    opacity: 0,
  },
  content: {
    borderRadius: theme.spacing(1),
    maxWidth: theme.spacing(100),
    position: 'relative',
    width: '100%',
  },
  contentOpen: {
    margin: theme.spacing(0, 2),
    [theme.breakpoints.up('sm')]: {
      margin: 0,
      width: '80%',
    },
    [theme.breakpoints.up('md')]: {
      width: '50%',
    },
  },
  contentOpenWidthAuto: {
    margin: theme.spacing(0, 2),
    [theme.breakpoints.up('sm')]: {
      margin: 0,
      width: 'auto',
    },
  },
  contentWrapper: {
    alignItems: 'center',
    bottom: 0,
    display: 'flex',
    height: '100%',
    justifyContent: 'center',
    left: 0,
    outline: 'none',
    position: 'fixed',
    right: 0,
    top: 0,
    width: '100%',
  },
  removeAction: {
    position: 'fixed',
    right: 0,
    top: theme.spacing(8),
    [theme.breakpoints.up('md')]: {
      position: 'absolute',
      right: theme.spacing(-10),
      top: theme.spacing(-1),
    },
  },
  welcomeMessageAudio: {
    paddingTop: theme.spacing(4),
  },
  welcomeMessageAudioNoOwner: {
    paddingBottom: theme.spacing(4),
  },
  welcomeMessageAudioTracker: {
    padding: theme.spacing(0, 4),
  },
  welcomeMessageText: {
    padding: theme.spacing(2, 0, 2, 0),
  },
  welcomeMessageTextTracker: {
    maxHeight: '100%',
    overflowX: 'hidden',
    overflowY: 'auto',
  },
  welcomeMessageTextTrackerOpen: {
    maxHeight: '60vh',
  },
  withTransition: {
    transition: theme.transitions.create('all', {
      duration: TRANSITION_DURATION,
      easing: theme.transitions.easing.easeInOut,
    }),
  },
}));

export type WelcomeMessageDialogProps = React.PropsWithChildren<{
  disableOwner?: boolean;
  displayWelcomeMessageOverlay?: boolean;
  onWelcomeMessageViewed: () => void;
  widthAuto?: boolean;
}>;

const WelcomeMessageDialog: React.FC<WelcomeMessageDialogProps> = (
  props: WelcomeMessageDialogProps
): JSX.Element => {
  const classes = useStyles();

  const {
    disableOwner = false,
    children,
    displayWelcomeMessageOverlay = false,
    onWelcomeMessageViewed,
    widthAuto = false,
  } = props;

  /**
   * 1. This flag is turned on a few milliseconds after the welcome message
   * is mounted, to let it load the cover image (for videos) and obtain the
   * coordinates and dimensions to position the clone and enable the animation
   */
  const [ready, setReady] = useState(false);

  /**
   * 2. This flag is turned on after being "ready" and the option to display
   * the welcome message as an overlay is given. It's turned on with a delay
   * of 750ms to make a smooth transition from the welcome message position
   * to a "full screen" dialog in the center of the screen
   */
  const [open, setOpen] = useState(false);

  /**
   * 3. This flag is turned on a few milliseconds after the overlay has
   * been opened and its transition has ended. This allows to control whether
   * the overlay has reached the opened state, to handle certain custom
   * styles needs only when the overlay is fully opened
   */
  const [opened, setOpened] = useState(false);

  /**
   * 4. Finally, this flag is turned on a few milliseconds after the overlay
   * has been closed by clicking the X button (the `open` flag changes from
   * true to false). This is the definitive and final step, where the overlay
   * is dismounted and the clone is removed from the DOM
   */
  const [closed, setClosed] = useState(false);

  /**
   * Using ref to know when the `open` flag changes from `false` to `true`
   * or viceversa, which let us change the `opened` or `closed` flags
   * respectively with a delay. A ref is used instead of `useState` to avoid
   * triggering unnecessary re-renders
   */
  const previousOpenValue = useRef(open);

  /**
   * Wrapper for the original content, used to obtain the position and
   * dimensions of the welcome message and assign them to the overlay
   * with a "cloned" version of it, to allow a smooth transition when
   * the overlay is opened
   */
  const childrenWrapperRef = useRef<HTMLDivElement>(null);

  /**
   * Ref to the overlay that wraps the "cloned" welcome message content
   * which is displayed on the overlay. This is used to determine if the
   * overlay was clicked, since we can't use the onClose prop of the Modal,
   * because we use this other overlay on top (for animation purposes)
   */
  const contentWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setReady(true);
    }, INITIAL_TIMEOUT);

    return () => {
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!ready) {
        return;
      }

      setOpen(displayWelcomeMessageOverlay);
    }, TRANSITION_DURATION - INITIAL_TIMEOUT);

    return () => {
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (previousOpenValue.current && !open) {
        setClosed(true);
      }

      if (!previousOpenValue.current && open) {
        onWelcomeMessageViewed();
        setOpened(true);
      }
    }, TRANSITION_DURATION + 50);

    return () => {
      previousOpenValue.current = open;
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onClose = () => {
    setOpen(false);
  };

  const onContentWrapperClicked = (e: React.MouseEvent) => {
    if (e.target === contentWrapperRef.current) {
      onClose();
    }
  };

  const renderWelcomeMessage = (
    clonedContent: React.ReactNode
  ): JSX.Element | undefined => {
    if (!(ready && childrenWrapperRef.current)) {
      return undefined;
    }

    const rect = childrenWrapperRef.current.getBoundingClientRect();
    if (!rect.height) {
      return undefined;
    }

    const { bottom, height, left, right, top, width } = rect;

    const closedStyles: CSSProperties = {
      backgroundColor: 'none',
      bottom,
      height,
      left,
      position: 'fixed',
      right,
      top,
      width,
    };

    if (closed) {
      return undefined;
    }

    return (
      <Modal
        BackdropComponent={Backdrop}
        BackdropProps={{
          open,
          style: {
            backgroundColor: 'var(--dialog-background-color-dark)',
            transitionDelay: `${TRANSITION_DURATION / 2}ms`,
          },
          timeout: TRANSITION_DURATION,
        }}
        open
      >
        <Box
          className={classNames(classes.withTransition, classes.contentWrapper)}
          onClickCapture={onContentWrapperClicked}
          style={open ? undefined : closedStyles}
          {...{ ref: contentWrapperRef }}
        >
          <Box
            className={classNames(classes.withTransition, classes.content, {
              [classes.contentOpen]: open && !widthAuto,
              [classes.contentOpenWidthAuto]: hasBeenOpened && widthAuto,
            })}
          >
            {clonedContent}

            <Fade
              in={open}
              style={{ transitionDelay: `${TRANSITION_DURATION}ms` }}
            >
              <Box className={classes.removeAction}>
                <RemoveAction onClick={onClose} />
              </Box>
            </Fade>
          </Box>
        </Box>
      </Modal>
    );
  };

  if (!displayWelcomeMessageOverlay) {
    return <>{children}</>;
  }

  const hasBeenOpened = (open || opened) && !closed;
  const currentlyOpen = opened && !closed;

  const customProps: Partial<WelcomeMessagePlayerProps> = {
    classes: {
      audio: classNames({
        [classes.welcomeMessageAudio]: hasBeenOpened,
        [classes.welcomeMessageAudioNoOwner]: disableOwner,
      }),
      audioTracker: classNames({
        [classes.welcomeMessageAudioTracker]: hasBeenOpened,
      }),
      text: classNames({
        [classes.welcomeMessageText]: hasBeenOpened,
      }),
      textTracker: classNames(classes.welcomeMessageTextTracker, {
        [classes.welcomeMessageTextTrackerOpen]: open ? opened : false,
      }),
    },
    /**
     * Disabling SizeMe "debounce" refresh mode because this breaks the
     * smooth "grow" animation on Safari, as changes on the height of the
     * welcome message's cover image are debounced until the width
     * has been affected and reaches its final state.
     */
    CoverTileProps: {
      disableDebounceRefreshMode: true,
    },
    displayOwner: hasBeenOpened,
    whiteBackground: currentlyOpen,
  };

  return (
    <>
      {React.Children.map(children, (child: any) => {
        const clonedContent = React.cloneElement(child, {
          ...child.props,
          ...customProps,
        });

        return (
          <>
            <Box
              className={classNames({ [classes.cloneHidden]: !closed })}
              {...{ ref: childrenWrapperRef }}
            >
              {children}
            </Box>
            {renderWelcomeMessage(clonedContent)}
          </>
        );
      })}
    </>
  );
};

export default WelcomeMessageDialog;
